<script lang="ts" setup>
import LocaleLink from '@/components/base/LocaleLink.vue'
import { computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useMailAuthStore, useVersionStore } from '@/stores'
import siteLogo from '@/assets/logo/logo_long_light_baked.svg'

defineOptions({ name: 'WebmailLayout' })

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const mailAuthStore = useMailAuthStore()
const versionStore = useVersionStore()

const mailboxName = computed(() => mailAuthStore.mailbox?.email || '')

const activeDelegationDomain = computed(() => {
  const domainIdParam = route.params.domainId
  if (!domainIdParam) return null
  const domainId = Number(domainIdParam)
  return mailAuthStore.delegations.find((d) => d.domain_id === domainId) ?? null
})

const handleLogout = () => {
  mailAuthStore.logout()
  router.push({ name: 'webmail' })
}

onMounted(() => {
  if (!mailAuthStore.isAuthenticated) {
    router.push({ name: 'webmail' })
    return
  }
  versionStore.startVersionWatcher()
})

onUnmounted(() => {
  versionStore.stopVersionWatcher()
})

</script>

<template>
  <div class="webmail-layout">
    <!-- Header -->
    <header class="webmail-header">
      <div class="header-content">
        <div class="header-left">
          <router-link :to="{ name: 'webmailDashboard' }" class="logo-link">
            <img :src="siteLogo" alt="ITSH" class="logo" />
            <span class="logo-text">Webmail</span>
          </router-link>
        </div>
        <div class="header-right">
          <div v-if="activeDelegationDomain" class="delegation-indicator">
            <v-icon icon="mdi-shield-account" size="16" />
            <span>{{ activeDelegationDomain.domain_name }}</span>
          </div>
          <div class="user-info">
            <v-icon icon="mdi-email" size="18" />
            <span class="user-email">{{ mailboxName }}</span>
          </div>
          <button class="logout-btn" @click="handleLogout">
            <v-icon icon="mdi-logout" size="18" />
            <span>{{ t('auth.logout') }}</span>
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="webmail-main">
      <slot />
    </main>

    <!-- Footer -->
    <footer class="webmail-footer">
      <div class="footer-content">
        <span>&copy; {{ new Date().getFullYear() }} IT-Service Harting</span>
        <LocaleLink to="impress">{{ t('landing.footer.imprint') }}</LocaleLink>
        <LocaleLink to="privacy">{{ t('landing.footer.privacy') }}</LocaleLink>
      </div>
    </footer>

  </div>
</template>

<style lang="scss" scoped>
.webmail-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0f1c 0%, #0f172a 50%, #1a1f35 100%);
}

.webmail-header {
  background: rgba(15, 23, 48, 0.9);
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  backdrop-filter: blur(12px);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 24px;
}

.header-left {
  display: flex;
  align-items: center;
}

.logo-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.logo {
  height: 32px;
  width: auto;
}

.logo-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.delegation-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: rgba(76, 175, 80, 0.12);
  border: 1px solid rgba(76, 175, 80, 0.25);
  border-radius: 6px;
  color: #66bb6a;
  font-size: 0.8rem;
  font-weight: 500;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(200, 218, 255, 0.7);
  font-size: 0.9rem;
}

.user-email {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 8px;
  color: #ef5350;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(244, 67, 54, 0.2);
  }
}

.webmail-main {
  flex: 1;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 24px;
}

.webmail-footer {
  background: rgba(15, 23, 48, 0.5);
  border-top: 1px solid rgba(120, 163, 255, 0.08);
  padding: 16px 24px;
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);

  a {
    color: rgba(200, 218, 255, 0.6);
    text-decoration: none;
    transition: color 0.15s ease;

    &:hover {
      color: rgba(200, 218, 255, 0.9);
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .webmail-layout {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
  }

  .webmail-header {
    background: rgba(255, 255, 255, 0.9);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .logo-text {
    color: rgba(15, 23, 42, 0.95);
  }

  .user-info {
    color: rgba(15, 23, 42, 0.7);
  }

  .webmail-footer {
    background: rgba(255, 255, 255, 0.5);
  }
}

/* Responsive */
@media (max-width: 640px) {
  .header-content {
    padding: 12px 16px;
  }

  .user-email {
    display: none;
  }

  .logout-btn span {
    display: none;
  }

  .webmail-main {
    padding: 16px;
  }
}
</style>
